<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import SalesReceiptReviewCorrectionsDialog from '../components/SalesReceiptReviewCorrectionsDialog.vue';
import SalesReceiptReviewWorkspace from '../components/SalesReceiptReviewWorkspace.vue';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import { useSalesDeliveryStart } from '../composables/useSalesDeliveryStart';
import { useSalesReceiptReviewPage } from '../composables/useSalesReceiptReviewPage';
import { useSalesReviewCheckCompletion } from '../composables/useSalesReviewCheckCompletion';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.salesTransactionId);
const start = useSalesDeliveryStart({ id, t, notify: useAlert });
const reviewCompletion = useSalesReviewCheckCompletion({
  id,
  transaction: start.transaction,
  t,
  notify: useAlert,
});
const page = useSalesReceiptReviewPage({
  id,
  notify: useAlert,
  route,
  router,
  reviewCompletion,
  start,
  t,
});
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesRegistrationPermissionState
      v-if="start.isForbidden.value"
      @back="page.goBack"
    />
    <template v-else>
      <LaisHeader
        :title="t('SALES.DELIVERY.REVIEW_RECEIPT.HEADER.TITLE')"
        :subtitle="
          page.headerSubtitle.value ||
          t('SALES.DELIVERY.REVIEW_RECEIPT.HEADER.SUBTITLE')
        "
        :breadcrumb="[
          { label: t('SALES.TITLE') },
          { label: t('SALES.REVIEW.TITLE') },
          { label: t('SALES.DELIVERY.REVIEW_RECEIPT.HEADER.TITLE') },
        ]"
        show-back
        :back-label="t('SALES.ACTIONS.BACK')"
        @back="page.goBack"
      />
      <p
        v-if="start.loading.value"
        class="mx-auto max-w-[1280px] px-4 py-12 text-center text-sm text-lais-text-muted sm:px-6 lg:px-10"
      >
        {{ t('SALES.DELIVERY.REVIEW_RECEIPT.LOADING') }}
      </p>
      <p
        v-else-if="start.loadError.value"
        class="mx-auto max-w-[1280px] px-4 py-12 text-center text-sm text-lais-text-muted sm:px-6 lg:px-10"
      >
        {{ t('SALES.DELIVERY.REVIEW_RECEIPT.ERRORS.LOAD') }}
      </p>
      <SalesReceiptReviewWorkspace
        v-else
        :before-select="page.beforeSelectBlock"
        :blocks="page.review.value.blocks"
        :can-complete="page.canComplete.value"
        :can-edit="start.canEdit.value"
        :can-remove="start.receipt.canRemove.value"
        :can-upload="start.receipt.canUpload.value"
        :document="start.receipt.document.value"
        :removing="start.receipt.removing.value"
        :saving="page.saving.value"
        :signature-date="page.signatureDate.value"
        :uploading="start.receipt.uploading.value"
        @complete="page.complete"
        @remove="start.receipt.remove"
        @save-date="page.saveDate"
        @save-exit="page.saveAndExit"
        @show-corrections="page.openCorrections"
        @update:signature-date="page.signatureDate.value = $event"
        @upload="page.uploadReceipt"
      />
      <SalesReceiptReviewCorrectionsDialog
        :context="page.review.value"
        :show="page.correctionsOpen.value"
        @close="page.closeCorrections"
      />
    </template>
  </div>
</template>
